"use client";

import { Info, Mic, PanelLeft, Phone, Plus, Search, Video } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

import { useSequence } from "../use-sequence";
import { AppWindow, Avatar, TypingDots } from "../visual-kit";

// =============================================================================

type Contact = { name: string; preview: string; time: string };

// typing → message, four times, the last message stays a little longer
const DURATIONS = [1100, 1900, 1000, 1700, 1100, 1700, 1000, 3400];

/**
 * Step 1: a messaging app where the client and I start talking.
 */
export function ChatVisual({ active }: { active: boolean }) {
  const t = useTranslations("Home.process.visuals.chat");
  const messages = t.raw("messages") as string[];
  const contacts = t.raw("contacts") as Contact[];
  const step = useSequence(active, DURATIONS);

  const visibleCount = Math.ceil(step / 2);
  const typingFrom =
    step % 2 === 0 && step < DURATIONS.length - 1
      ? (step / 2) % 2 === 0
        ? "client"
        : "me"
      : null;

  return (
    <AppWindow>
      <div className="flex size-full">
        {/* Conversations */}
        <aside className="flex w-[27%] shrink-0 flex-col border-r border-black/[0.07] p-[1.4cqw]">
          <div className="flex items-center justify-between">
            <span className="text-[1.3cqw] font-semibold">{t("title")}</span>
            <span className="flex gap-[0.8cqw] text-black/60">
              <Plus className="size-[1.5cqw]" />
              <PanelLeft className="size-[1.5cqw]" />
            </span>
          </div>
          <div className="mt-[1.1cqw] flex items-center gap-[0.7cqw] rounded-[0.8cqw] border border-black/10 px-[0.8cqw] py-[0.65cqw] text-[1.05cqw] text-black/45">
            <Search className="size-[1.25cqw]" />
            {t("search")}
          </div>
          <ul className="mt-[1cqw] flex flex-col gap-[0.35cqw]">
            {contacts.map((contact, index) => (
              <li
                key={contact.name}
                className={cn(
                  "flex items-center gap-[0.8cqw] rounded-[0.8cqw] px-[0.6cqw] py-[0.6cqw]",
                  index === 0 && "bg-black/[0.045]"
                )}
              >
                <Avatar
                  name={contact.name}
                  className="size-[2.9cqw] text-[1cqw]"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-[0.4cqw]">
                    <span className="truncate text-[1.08cqw] font-semibold">
                      {contact.name}
                    </span>
                    <span className="text-[0.82cqw] text-black/45">
                      {contact.time}
                    </span>
                  </span>
                  <span className="block truncate text-[0.98cqw] text-black/55">
                    {contact.preview}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </aside>

        {/* Conversation */}
        <section className="flex min-w-0 flex-1 flex-col">
          <header className="flex items-center justify-between border-b border-black/[0.07] px-[1.4cqw] py-[1cqw]">
            <span className="flex items-center gap-[0.9cqw]">
              <Avatar
                name={t("client")}
                className="size-[3cqw] text-[1.05cqw]"
              />
              <span className="text-[1.2cqw] font-semibold">{t("client")}</span>
            </span>
            <span className="flex gap-[1.1cqw] text-black/70">
              <Phone className="size-[1.5cqw]" />
              <Video className="size-[1.5cqw]" />
              <Info className="size-[1.5cqw]" />
            </span>
          </header>

          <div className="flex flex-1 flex-col justify-end gap-[1cqw] overflow-hidden px-[1.6cqw] py-[1.4cqw]">
            <AnimatePresence initial={false}>
              {messages.slice(0, visibleCount).map((message, index) => {
                const isMe = index % 2 === 1;
                return (
                  <motion.div
                    key={`${index}-${message.slice(0, 8)}`}
                    layout
                    initial={{ opacity: 0, y: 14, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.15 } }}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    className={cn(
                      "flex items-end gap-[0.7cqw]",
                      isMe && "flex-row-reverse"
                    )}
                  >
                    {!isMe && (
                      <Avatar
                        name={t("client")}
                        className="size-[2.2cqw] text-[0.8cqw]"
                      />
                    )}
                    <p
                      className={cn(
                        "max-w-[64%] rounded-[1.4cqw] px-[1.2cqw] py-[0.75cqw] text-[1.08cqw] leading-[1.35]",
                        isMe
                          ? "rounded-br-[0.4cqw] bg-[#141414] text-white"
                          : "rounded-bl-[0.4cqw] bg-[#f1f1f3]"
                      )}
                    >
                      {message}
                    </p>
                  </motion.div>
                );
              })}
              {typingFrom && (
                <motion.div
                  key={`typing-${step}`}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, transition: { duration: 0.1 } }}
                  className={cn(
                    "flex items-end gap-[0.7cqw]",
                    typingFrom === "me" && "flex-row-reverse"
                  )}
                >
                  {typingFrom === "client" && (
                    <Avatar
                      name={t("client")}
                      className="size-[2.2cqw] text-[0.8cqw]"
                    />
                  )}
                  <span
                    className={cn(
                      "rounded-[1.4cqw] px-[1.2cqw] py-[1cqw]",
                      typingFrom === "me"
                        ? "bg-[#141414] text-white"
                        : "bg-[#f1f1f3] text-black"
                    )}
                  >
                    <TypingDots />
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <footer className="flex items-center justify-between border-t border-black/[0.07] px-[1.6cqw] py-[1.1cqw] text-[1.08cqw] text-black/45">
            {t("placeholder")}
            <Mic className="size-[1.5cqw] text-black/70" />
          </footer>
        </section>
      </div>
    </AppWindow>
  );
}

// =============================================================================
